import React, { useEffect, useState, useCallback } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import axios from 'axios'
import jwtDecode from 'jwt-decode'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import ReactGA from 'react-ga'

import Routes from './Routes'
import Toast from './components/Loadboard/Searches/Toast'

import { DarkModeProvider } from './context/DarkModeContext'
import { SubscriptionProvider } from './context/SubscriptionContext'
import { SidebarProvider } from './context/SidebarContext'
import { getCurrentUser } from './services/authService'
import posthogService from './services/posthogService'

import TelegramGroupConnect from './components/TelegramGroupConnect/TelegramGroupConnect'
import TeamInvitationModal from './components/TeamInvitationModal'

const queryClient = new QueryClient()

function App() {
	const [user, setUser] = useState(null)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [urlData, setUrlData] = useState(null)
	const [toastShow, setToastShow] = useState(false)
	const [toastMessage, setToastMessage] = useState('')

	const queryString = window.location.search
	const urlParams = new URLSearchParams(queryString)
	const token = urlParams.get('token')
	const chatId = urlParams.get('chatId')

	const refreshToken = useCallback(async () => {
		const tokenRaw = localStorage.getItem('jwtToken')
		if (!tokenRaw) return

		try {
			const decodedToken = jwtDecode(tokenRaw)
			const currentTime = Date.now() / 1000
			const tokenAge = currentTime - decodedToken.iat
			const threeHoursInSeconds = 3 * 60 * 60
			const subscription = decodedToken?.subscriptionType

			if (decodedToken?.deviceLimitReached) return

			if (!subscription || tokenAge >= threeHoursInSeconds) {
				const response = await axios.post('/api/user/refresh-token/web')
				const newToken = response.data.token

				if (response.data.success) {
					localStorage.setItem('jwtToken', newToken)
				} else {
					console.log('Failed to refresh token')
				}
			}
		} catch (error) {
			console.error('Error in token refresh process:', error)
		}
	}, [])

	useEffect(() => {
		const intervalId = setInterval(() => {
			if (user) refreshToken()
		}, 2 * 60 * 1000)

		return () => clearInterval(intervalId)
	}, [user, refreshToken])

	useEffect(() => {
		const fetchUserAndHandleTelegramLink = async () => {
			if (token && chatId) {
				try {
					const url = '/api/user/link-telegram-group'

					const response = await axios.get(url, { params: { token, chatId } })

					console.log('response', response?.data?.groupName)

					setIsModalOpen(true)
					setUrlData({ token, chatId, groupName: response.data.groupName })
				} catch (error) {
					const user = getCurrentUser()

					console.log('Error linking Telegram group:', error)

					if (user) {
						let errorMessage = 'Error linking Telegram group'
						if (error?.response?.data?.message) {
							errorMessage = error.response.data.message
						}

						setToastShow(true)
						setToastMessage(errorMessage)

						setTimeout(() => {
							setToastShow(false)
						}, 5000)
					}
				}
			}
		}

		fetchUserAndHandleTelegramLink()
	}, [])

	useEffect(() => {
		// Initialize analytics first
		ReactGA.initialize('UA-180688059-1')
		ReactGA.pageview(window.location.pathname + window.location.search)

		// Initialize PostHog
		posthogService.initialize()

		// Then fetch and identify user (after PostHog is initialized)
		const fetchUser = async () => {
			const user = getCurrentUser()

			if (user) {
				refreshToken()
				setUser(user)
				window.clarity('identify', user?.email)

				if (window.$crisp) {
					window.$crisp.push(['set', 'user:email', user?.email])
					window.$crisp.push(['set', 'user:nickname', user?.email])
				}

				// Identify user in PostHog (after initialization)
				console.log('Attempting to identify user in PostHog:', user)
				posthogService.identify(user._id || user.email, {
					email: user.email,
					name: user.name,
					subscriptionType: user.subscriptionType,
					createdAt: user.createdAt,
				})
			}
		}

		fetchUser()
	}, [])

	return (
		<QueryClientProvider client={queryClient}>
			<DarkModeProvider>
				<SubscriptionProvider>
					<SidebarProvider>
						<BrowserRouter>
							<ToastContainer />
							<Routes user={user} />

							<TelegramGroupConnect
								isModalOpen={isModalOpen}
								closeModal={() => setIsModalOpen(false)}
								urlData={urlData}
							/>

							{user && <TeamInvitationModal />}

							{toastShow && <Toast message={toastMessage} error={true} />}
						</BrowserRouter>
					</SidebarProvider>
				</SubscriptionProvider>
			</DarkModeProvider>
		</QueryClientProvider>
	)
}

export default App
